import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { simulationsAPI, tauxAPI, dashboardAPI, userAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Building2, Calculator, AlertTriangle, TrendingUp, Info, RefreshCw } from 'lucide-react';

const URSSAFPage = () => {
  const { user, setUser } = useAuth();
  const [taux, setTaux] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [chiffreAffaires, setChiffreAffaires] = useState('');
  const [typeActivite, setTypeActivite] = useState(() => {
    return user?.type_activite || localStorage.getItem('ae_type_activite') || 'BIC_SERVICE';
  });
  const [isTvaSubject, setIsTvaSubject] = useState(() => {
    return user?.is_tva_subject !== undefined ? user.is_tva_subject : true;
  });
  const [versementLiberatoire, setVersementLiberatoire] = useState(() => {
    return user?.versement_liberatoire !== undefined ? user.versement_liberatoire : false;
  });
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadTaux();
    loadStats();
  }, []);
  
  useEffect(() => {
    localStorage.setItem('ae_type_activite', typeActivite);
    saveUserSettings({ type_activite: typeActivite });
  }, [typeActivite]);

  useEffect(() => {
    saveUserSettings({ is_tva_subject: isTvaSubject });
  }, [isTvaSubject]);

  useEffect(() => {
    saveUserSettings({ versement_liberatoire: versementLiberatoire });
  }, [versementLiberatoire]);

  const saveUserSettings = async (settings) => {
    try {
      const response = await userAPI.updateSettings(settings);
      if (setUser) setUser(response.data);
    } catch (error) {
      // Error handled silently
    }
  };

  const loadTaux = async () => {
    try {
      const response = await tauxAPI.get();
      setTaux(response.data);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };
  
  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      // Error handled silently
    }
  };

  // Récupérer le bon CA selon le statut TVA
  const getCaEncaisse = () => {
    if (!stats) return { ttc: 0, ht: 0 };
    return {
      ttc: stats.annee?.ca_encaisse || stats.chiffre_affaires || 0,
      ht: stats.annee?.ca_encaisse_ht || (stats.chiffre_affaires ? stats.chiffre_affaires / 1.2 : 0)
    };
  };

  const caData = getCaEncaisse();
  // Si assujetti TVA, on prend le HT. Sinon (franchise), on prend le TTC
  const caBase = isTvaSubject ? caData.ht : caData.ttc;
  
  const handleUseEncaisse = () => {
    setChiffreAffaires(caBase.toFixed(2));
    toast.success(isTvaSubject 
      ? 'CA encaissé HT récupéré (vous êtes assujetti TVA)' 
      : 'CA encaissé TTC récupéré (vous êtes en franchise de TVA)'
    );
  };

  // Taux URSSAF selon activité
  const getTauxURSSAF = () => {
    if (!taux) return 21.2;
    switch(typeActivite) {
      case 'BIC_VENTE': return taux.taux_urssaf_bic_vente || 12.3;
      case 'BIC_SERVICE': return taux.taux_urssaf_bic_service || 21.2;
      case 'BNC': return taux.taux_urssaf_bnc || 25.6;
      case 'BNC_CIPAV': return taux.taux_urssaf_bnc_cipav || 23.2;
      default: return 21.2;
    }
  };

  // Taux Versement Libératoire IR
  const getTauxVL = () => {
    if (!versementLiberatoire) return 0;
    switch(typeActivite) {
      case 'BIC_VENTE': return 1.0;
      case 'BIC_SERVICE': return 1.7;
      case 'BNC': return 2.2;
      case 'BNC_CIPAV': return 2.2;
      default: return 1.7;
    }
  };

  // Taux CFP (Contribution Formation Professionnelle)
  const getTauxCFP = () => {
    switch(typeActivite) {
      case 'BIC_VENTE': return 0.1;
      case 'BIC_SERVICE': return 0.3;
      case 'BNC': return 0.2;
      case 'BNC_CIPAV': return 0.2;
      default: return 0.2;
    }
  };

  const tauxURSSAF = getTauxURSSAF();
  const tauxVL = getTauxVL();
  const tauxCFP = getTauxCFP();
  const tauxTotal = tauxURSSAF + tauxVL + tauxCFP;

  const handleSimulate = async (e) => {
    e.preventDefault();
    if (!chiffreAffaires || parseFloat(chiffreAffaires) <= 0) {
      toast.error('Veuillez entrer un chiffre d\'affaires valide');
      return;
    }
    setSimulating(true);
    
    const ca = parseFloat(chiffreAffaires);
    const cotisationURSSAF = ca * (tauxURSSAF / 100);
    const cotisationVL = ca * (tauxVL / 100);
    const cotisationCFP = ca * (tauxCFP / 100);
    const cotisationTotale = cotisationURSSAF + cotisationVL + cotisationCFP;
    
    // Plafonds CA
    const plafond = typeActivite === 'BIC_VENTE' ? (taux?.plafond_ca_bic_vente || 203100) : (taux?.plafond_ca_bic_service || 83600);
    const pourcentagePlafond = (ca / plafond) * 100;
    
    setResult({
      chiffre_affaires: ca,
      base_calcul: isTvaSubject ? 'HT' : 'TTC',
      cotisation_urssaf: cotisationURSSAF,
      cotisation_vl: cotisationVL,
      cotisation_cfp: cotisationCFP,
      cotisation_totale: cotisationTotale,
      taux_urssaf: tauxURSSAF,
      taux_vl: tauxVL,
      taux_cfp: tauxCFP,
      taux_total: tauxTotal,
      revenu_net: ca - cotisationTotale,
      plafond_ca: plafond,
      pourcentage_plafond: pourcentagePlafond,
      alerte_plafond: pourcentagePlafond > 80
    });
    
    setSimulating(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const activityTypes = [
    { value: 'BIC_VENTE', label: 'Vente de marchandises (BIC)', taux: taux?.taux_urssaf_bic_vente || 12.3, color: 'bg-emerald-100 border-emerald-300 text-emerald-800' },
    { value: 'BIC_SERVICE', label: 'Prestation de services (BIC)', taux: taux?.taux_urssaf_bic_service || 21.2, color: 'bg-blue-100 border-blue-300 text-blue-800' },
    { value: 'BNC', label: 'Libéral régime général (BNC)', taux: taux?.taux_urssaf_bnc || 25.6, color: 'bg-amber-100 border-amber-300 text-amber-800' },
    { value: 'BNC_CIPAV', label: 'Libéral CIPAV (BNC)', taux: taux?.taux_urssaf_bnc_cipav || 23.2, color: 'bg-purple-100 border-purple-300 text-purple-800' }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48"></div>
          <div className="h-64 bg-muted rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="urssaf-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Cotisations URSSAF</h1>
          <p className="text-muted-foreground">Simulez vos cotisations sociales selon votre activité</p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
            <Label htmlFor="tva-toggle-urssaf" className="text-sm font-medium cursor-pointer">
              {isTvaSubject ? 'Assujetti TVA (HT)' : 'Franchise TVA (TTC)'}
            </Label>
            <Switch 
              id="tva-toggle-urssaf"
              checked={isTvaSubject}
              onCheckedChange={setIsTvaSubject}
            />
          </div>
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
            <Label htmlFor="vl-toggle-urssaf" className="text-sm font-medium cursor-pointer">
              {versementLiberatoire ? `VL IR (${tauxVL}%)` : 'Sans VL'}
            </Label>
            <Switch 
              id="vl-toggle-urssaf"
              checked={versementLiberatoire}
              onCheckedChange={setVersementLiberatoire}
            />
          </div>
        </div>
      </div>

      {/* Info importante HT/TTC */}
      <Card className={isTvaSubject ? "border-blue-200 bg-blue-50" : "border-amber-200 bg-amber-50"}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className={`h-5 w-5 mt-0.5 ${isTvaSubject ? 'text-blue-600' : 'text-amber-600'}`} />
            <div>
              <p className={`font-semibold ${isTvaSubject ? 'text-blue-900' : 'text-amber-900'}`}>
                {isTvaSubject ? 'Vous êtes assujetti à la TVA' : 'Vous êtes en franchise de TVA'}
              </p>
              <p className={`text-sm ${isTvaSubject ? 'text-blue-800' : 'text-amber-800'}`}>
                {isTvaSubject 
                  ? 'Vos cotisations URSSAF sont calculées sur le montant HT (hors TVA) de vos encaissements. La TVA collectée appartient à l\'État, pas à votre CA.'
                  : 'Vos cotisations URSSAF sont calculées sur le montant TTC de vos encaissements (vous ne collectez pas de TVA).'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Simulateur */}
        <Card>
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Simulateur de cotisations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSimulate} className="space-y-6">
              {/* Type d'activité */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Type d'activité</Label>
                <div className="grid grid-cols-1 gap-2">
                  {activityTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setTypeActivite(type.value)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        typeActivite === type.value
                          ? type.color + ' border-2 shadow-sm'
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`urssaf-type-${type.value}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{type.label}</span>
                        <Badge variant="secondary" className={typeActivite === type.value ? type.color : ''}>
                          {type.taux}%
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ca" className="text-base font-semibold">
                  Chiffre d'affaires encaissé {isTvaSubject ? '(HT)' : '(TTC)'} (€)
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="ca"
                    type="number"
                    step="0.01"
                    min="0"
                    value={chiffreAffaires}
                    onChange={(e) => setChiffreAffaires(e.target.value)}
                    placeholder="Ex: 25000"
                    required
                    className="text-lg flex-1"
                    data-testid="urssaf-ca"
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={handleUseEncaisse}
                    className="whitespace-nowrap"
                    data-testid="urssaf-use-encaisse"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Récupérer
                  </Button>
                </div>
                {caBase > 0 && (
                  <p className="text-xs text-emerald-600 font-medium">
                    CA encaissé {isTvaSubject ? 'HT' : 'TTC'} : {formatCurrency(caBase)}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {isTvaSubject 
                    ? 'Montant HT car vous êtes assujetti TVA (la TVA n\'est pas votre CA)'
                    : 'Montant TTC car vous êtes en franchise de TVA'
                  }
                </p>
              </div>

              {/* Récap taux */}
              <div className="p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                <div className="flex justify-between">
                  <span>URSSAF</span>
                  <span className="font-medium">{tauxURSSAF}%</span>
                </div>
                {versementLiberatoire && (
                  <div className="flex justify-between text-violet-700">
                    <span>IR (Versement Libératoire)</span>
                    <span className="font-medium">{tauxVL}%</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>CFP (Formation)</span>
                  <span className="font-medium">{tauxCFP}%</span>
                </div>
                <div className="flex justify-between font-bold pt-1 border-t">
                  <span>TOTAL</span>
                  <span>{tauxTotal.toFixed(1)}%</span>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={simulating || !chiffreAffaires}
                data-testid="urssaf-calculate"
              >
                {simulating ? 'Calcul en cours...' : 'Calculer mes cotisations'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Résultats */}
        <Card>
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Résultat de la simulation
            </CardTitle>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-6">
                {/* Résumé principal */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted rounded-lg text-center">
                    <p className="text-sm text-muted-foreground mb-1">CA {result.base_calcul}</p>
                    <p className="text-2xl font-bold">{formatCurrency(result.chiffre_affaires)}</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg text-center border border-red-200">
                    <p className="text-sm text-red-700 mb-1">Cotisations totales</p>
                    <p className="text-2xl font-bold text-red-700">{formatCurrency(result.cotisation_totale)}</p>
                  </div>
                </div>

                <div className="p-4 bg-emerald-50 rounded-lg text-center border border-emerald-200">
                  <p className="text-sm text-emerald-700 mb-1">
                    {versementLiberatoire ? 'Revenu net (après IR)' : 'Revenu net avant IR'}
                  </p>
                  <p className="text-3xl font-bold text-emerald-700">{formatCurrency(result.revenu_net)}</p>
                </div>

                {/* Détails cotisations */}
                <div className="space-y-3">
                  <p className="font-semibold text-sm text-gray-600">Détail des cotisations</p>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">URSSAF ({result.taux_urssaf}%)</span>
                    <span className="font-semibold">{formatCurrency(result.cotisation_urssaf)}</span>
                  </div>
                  {versementLiberatoire && (
                    <div className="flex justify-between items-center py-2 border-b text-violet-700">
                      <span>IR - Versement Libératoire ({result.taux_vl}%)</span>
                      <span className="font-semibold">{formatCurrency(result.cotisation_vl)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">CFP ({result.taux_cfp}%)</span>
                    <span className="font-semibold">{formatCurrency(result.cotisation_cfp)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">Plafond CA annuel</span>
                    <span className="font-semibold">{formatCurrency(result.plafond_ca)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-muted-foreground">Utilisation du plafond</span>
                    <span className="font-semibold">{result.pourcentage_plafond.toFixed(1)}%</span>
                  </div>
                </div>

                {/* Progress bar plafond */}
                <div>
                  <Progress 
                    value={Math.min(result.pourcentage_plafond, 100)} 
                    className={`h-3 ${result.alerte_plafond ? 'bg-red-100' : 'bg-muted'}`}
                  />
                  {result.alerte_plafond && (
                    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-amber-800">Attention au plafond !</p>
                        <p className="text-sm text-amber-700">
                          Vous approchez du plafond de CA. Au-delà, vous perdez le statut auto-entrepreneur.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Entrez votre chiffre d'affaires et cliquez sur "Calculer" pour voir le résultat</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Taux en vigueur */}
      <Card>
        <CardHeader>
          <CardTitle className="font-['Manrope'] flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Taux de cotisations 2026
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Type d'activité</th>
                  <th className="text-center py-2">URSSAF</th>
                  <th className="text-center py-2">VL IR</th>
                  <th className="text-center py-2">CFP</th>
                  <th className="text-center py-2 font-bold">Total sans VL</th>
                  <th className="text-center py-2 font-bold">Total avec VL</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2">Vente (BIC)</td>
                  <td className="text-center">12.3%</td>
                  <td className="text-center text-violet-600">1.0%</td>
                  <td className="text-center">0.1%</td>
                  <td className="text-center font-semibold">12.4%</td>
                  <td className="text-center font-semibold">13.4%</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Services (BIC)</td>
                  <td className="text-center">21.2%</td>
                  <td className="text-center text-violet-600">1.7%</td>
                  <td className="text-center">0.3%</td>
                  <td className="text-center font-semibold">21.5%</td>
                  <td className="text-center font-semibold">23.2%</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Libéral (BNC)</td>
                  <td className="text-center">25.6%</td>
                  <td className="text-center text-violet-600">2.2%</td>
                  <td className="text-center">0.2%</td>
                  <td className="text-center font-semibold">25.8%</td>
                  <td className="text-center font-semibold">28.0%</td>
                </tr>
                <tr>
                  <td className="py-2">Libéral CIPAV</td>
                  <td className="text-center">23.2%</td>
                  <td className="text-center text-violet-600">2.2%</td>
                  <td className="text-center">0.2%</td>
                  <td className="text-center font-semibold">23.4%</td>
                  <td className="text-center font-semibold">25.6%</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
            <p><strong>Important :</strong> Si vous êtes assujetti à la TVA, vos cotisations sont calculées sur le montant <strong>HT</strong> de vos encaissements. Si vous êtes en franchise de TVA, elles sont calculées sur le montant <strong>TTC</strong>.</p>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Source : autoentrepreneur.urssaf.fr — Taux et plafonds 2026 (BNC 25,6 % depuis le 1er janvier 2026 ; plafonds 203 100 € / 83 600 €)
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default URSSAFPage;
